'use client';

import { ExternalLink, Globe, Link2 } from 'lucide-react';
import Image from 'next/image';
import type { MouseEventHandler } from 'react';
import { CuteSpinner } from '@/components/common/CuteSpinner';
import { SummarizerActionControls } from '@/components/summarizer/SummarizerActionControls';
import { DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator } from '@/components/ui/dropdown-menu';
import type { LinkMetadata } from './types';

interface MetadataPreviewProperties {
  allowAdvancedControls: boolean;
  displayUrl: string;
  domain: string;
  faviconUrl?: string | null | undefined;
  isChatEnabled: boolean;
  isChatLoading: boolean;
  isSummarizing: boolean;
  metadata: LinkMetadata | null;
  onChat: () => void;
  onExplain: () => void;
  onFactCheck: () => void;
  onOpenSettingsMenu: () => void;
  onSummarize: MouseEventHandler<HTMLButtonElement>;
  shouldShowSummarizeControls: boolean;
  summaryButtonLabel: string;
}

export function MetadataPreview(properties: MetadataPreviewProperties) {
  const {
    metadata,
    domain,
    faviconUrl,
    displayUrl,
    shouldShowSummarizeControls,
    summaryButtonLabel,
    onSummarize,
    isSummarizing,
    isChatEnabled,
    isChatLoading,
    onChat,
    onExplain,
    onFactCheck,
    allowAdvancedControls,
    onOpenSettingsMenu,
  } = properties;

  return (
    <div className="p-5 sm:p-6 space-y-3.5">
      <div className="flex items-center gap-2.5 text-xs">
        {faviconUrl ? (
          <Image
            src={faviconUrl}
            alt={domain}
            width={16}
            height={16}
            className="h-4 w-4 rounded-sm ring-1 ring-black/5 dark:ring-white/10"
            unoptimized
            onError={(event) => {
              event.currentTarget.style.display = 'none';
            }}
          />
        ) : null}
        <Globe className="w-3.5 h-3.5 text-muted-foreground" aria-hidden="true" />
        <span className="font-medium text-muted-foreground">{metadata?.siteName || domain}</span>
        <div className="ml-auto flex items-center gap-2">
          <div className="flex items-center gap-1 opacity-0 transition-all duration-200 group-hover/link-preview:opacity-100">
            <Link2 className="w-3 h-3 text-muted-foreground" aria-hidden="true" />
            <ExternalLink className="w-3 h-3 text-primary" aria-hidden="true" />
          </div>
          {shouldShowSummarizeControls ? (
            <SummarizerActionControls
              label={summaryButtonLabel}
              onPrimaryClick={onSummarize}
              disabled={isSummarizing}
              secondaryAction={{
                type: 'menu',
                ariaLabel: 'More link assistant actions',
                renderMenu: () => (
                  <DropdownMenuContent align="end" className="w-48">
                    <DropdownMenuItem
                      disabled={!isChatEnabled || isChatLoading}
                      onSelect={() => {
                        if (!isChatEnabled || isChatLoading) return;
                        onChat();
                      }}
                    >
                      {isChatLoading ? (
                        <span className="inline-flex items-center gap-2">
                          <CuteSpinner size={12} className="border-current/40 border-t-current" />
                          Preparing chat…
                        </span>
                      ) : (
                        'Chat'
                      )}
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      disabled={!isChatEnabled || isChatLoading}
                      onSelect={() => {
                        if (!isChatEnabled || isChatLoading) return;
                        onExplain();
                      }}
                    >
                      Explain
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      disabled={!isChatEnabled || isChatLoading}
                      onSelect={() => {
                        if (!isChatEnabled || isChatLoading) return;
                        onFactCheck();
                      }}
                    >
                      Fact-Check
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      disabled={!allowAdvancedControls}
                      onSelect={() => {
                        if (!allowAdvancedControls) return;
                        onOpenSettingsMenu();
                      }}
                    >
                      Summarize…
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                ),
              }}
              primaryClassName="h-8 px-3 text-xs font-semibold shadow-sm"
              groupClassName="shadow-none"
            />
          ) : null}
        </div>
      </div>

      {metadata?.title ? (
        <h3 className="font-semibold text-base sm:text-lg leading-snug text-foreground line-clamp-2 group-hover/link-preview:text-primary dark:group-hover/link-preview:text-primary transition-colors duration-200">
          {metadata.title}
        </h3>
      ) : null}

      {metadata?.description ? (
        <p className="text-sm text-muted-foreground line-clamp-2 sm:line-clamp-3 leading-relaxed">
          {metadata.description}
        </p>
      ) : null}

      <div className="flex items-center gap-1.5 text-xs text-muted-foreground/70 truncate">
        <span className="truncate">{displayUrl}</span>
      </div>
    </div>
  );
}
